import type { StreamingMessage, StreamingMessageId, StreamingMessagesRecord } from "./streamingTypes";

/**
 * Ordering key for a branch. Every message rebuilt from a recording carries the time its
 * turn started; the one turn that has no recording yet — the one still being generated,
 * which a reload picks up from the playback stream — carries no time at all. That turn is
 * the most recent branch by definition, so an absent time sorts newest rather than at 1970.
 */
const branchStart = (message: StreamingMessage | undefined): number => {
  if (!message) return 0;
  return message.submittedAt?.getTime() ?? Number.POSITIVE_INFINITY;
};

/** Oldest first. Compares rather than subtracts, so two open-ended branches keep their order. */
const byOldestFirst = (a: StreamingMessage | undefined, b: StreamingMessage | undefined): number => {
  const ta = branchStart(a);
  const tb = branchStart(b);
  if (ta === tb) return 0;
  return ta < tb ? -1 : 1;
};

/**
 * Finds all sibling messages (messages sharing the same parentMessageId)
 * for a given message. Returns siblings sorted by creation order.
 */
export function getSiblings(
  messages: StreamingMessagesRecord,
  messageId: StreamingMessageId,
  chatMessageIds?: StreamingMessageId[]
): StreamingMessageId[] {
  const msg = messages[messageId];
  if (!msg) return [];

  const parentId = msg.parentMessageId;
  const iterateIds = chatMessageIds ?? (Object.keys(messages) as StreamingMessageId[]);

  if (!parentId) {
    const chatId = msg.chatId;
    const rootSiblings: StreamingMessageId[] = [];
    for (const id of iterateIds) {
      const m = messages[id];
      if (m && !m.parentMessageId && m.chatId === chatId) {
        rootSiblings.push(id);
      }
    }
    if (rootSiblings.length <= 1) return [messageId];
    rootSiblings.sort((a, b) => byOldestFirst(messages[a], messages[b]));
    return rootSiblings;
  }

  const targetChatId = msg.chatId;

  const siblings: StreamingMessageId[] = [];
  for (const id of iterateIds) {
    const m = messages[id];
    if (m?.parentMessageId === parentId && m.chatId === targetChatId) {
      siblings.push(id);
    }
  }

  siblings.sort((a, b) => byOldestFirst(messages[a], messages[b]));

  return siblings;
}

/**
 * Returns the position of a message among its siblings.
 * Returns { current: 1-based index, total: count } or null if no siblings.
 */
export function getBranchPosition(
  messages: StreamingMessagesRecord,
  messageId: StreamingMessageId,
  chatMessageIds?: StreamingMessageId[]
): { current: number; total: number } | null {
  const siblings = getSiblings(messages, messageId, chatMessageIds);
  if (siblings.length <= 1) return null;

  const index = siblings.indexOf(messageId);
  if (index === -1) return null;
  return { current: index + 1, total: siblings.length };
}

/** Sentinel key for root-level branch selections. */
export const ROOT_BRANCH_KEY = "__root__" as const;

/**
 * Computes the active conversation path by walking from root to leaf.
 * At each fork point, follows the branch recorded in `selections`;
 * falls back to the most recently created child when no selection exists.
 */
export function computeActivePath(
  messages: StreamingMessagesRecord,
  chatMessageIds: StreamingMessageId[],
  selections: Map<string, StreamingMessageId>,
  hasBranches?: boolean,
  defaultLeaf?: StreamingMessageId
): StreamingMessageId[] {
  const roots: StreamingMessageId[] = [];
  const childrenOf = new Map<string, StreamingMessageId[]>();
  const positionOf = new Map<string, number>();

  for (let i = 0; i < chatMessageIds.length; i++) {
    const id = chatMessageIds[i];
    if (!id) continue;
    positionOf.set(id, i);
    const msg = messages[id];
    if (!msg) continue;
    if (!msg.parentMessageId) {
      roots.push(id);
    } else {
      const existing = childrenOf.get(msg.parentMessageId) ?? [];
      existing.push(id);
      childrenOf.set(msg.parentMessageId, existing);
    }
  }

  const [firstRoot] = roots;
  if (firstRoot === undefined) {
    // No roots are resolved yet — messages may still be loading.
    // Return only IDs that are actually present to avoid undefined-access downstream.
    return chatMessageIds.filter((id) => id && messages[id] !== undefined);
  }

  // Legacy fork fallback: only for chats that don't support branching.
  // Branching-aware chats with multiple roots (e.g., editing first message)
  // should use normal branch selection below.
  // selections.size > 0 means the user actively switched branches, so treat it as branching-aware
  // even if hasBranches hasn't been set yet (it's undefined during live streaming).
  if (!hasBranches && selections.size === 0 && roots.length === chatMessageIds.length && childrenOf.size === 0) {
    return chatMessageIds;
  }

  // When a default leaf is provided, build an ancestor set so we follow
  // the leaf's branch at each fork (selections take priority at each node).
  let ancestorSet: Set<string> | undefined;
  if (defaultLeaf) {
    ancestorSet = new Set<string>();
    let cur: StreamingMessageId | undefined = defaultLeaf;
    while (cur) {
      ancestorSet.add(cur);
      const parent: StreamingMessageId | undefined = messages[cur]?.parentMessageId;
      cur = parent ? parent : undefined;
    }
  }

  const selectedRoot = selections.get(ROOT_BRANCH_KEY);
  let root: StreamingMessageId;
  if (selectedRoot && roots.includes(selectedRoot)) {
    root = selectedRoot;
  } else if (ancestorSet) {
    root = roots.find((r) => ancestorSet.has(r)) ?? pickNewest(roots, messages, positionOf) ?? firstRoot;
  } else {
    root = pickNewest(roots, messages, positionOf) ?? firstRoot;
  }

  const path: StreamingMessageId[] = [];
  let current: StreamingMessageId | undefined = root;

  while (current) {
    path.push(current);
    const children = childrenOf.get(current);
    if (!children || children.length === 0) break;

    if (children.length === 1) {
      current = children[0];
    } else {
      const selected = selections.get(current);
      if (selected && children.includes(selected)) {
        current = selected;
      } else if (ancestorSet) {
        current = children.find((c) => ancestorSet.has(c)) ?? pickNewest(children, messages, positionOf) ?? children[0];
      } else {
        current = pickNewest(children, messages, positionOf) ?? children[0];
      }
    }
  }

  return path;
}

function pickNewest(
  ids: StreamingMessageId[],
  messages: StreamingMessagesRecord,
  positionOf: Map<string, number>
): StreamingMessageId | undefined {
  return ids.toSorted((a, b) => {
    const byStart = byOldestFirst(messages[b], messages[a]);
    if (byStart !== 0) return byStart;
    return (positionOf.get(b) ?? -1) - (positionOf.get(a) ?? -1);
  })[0];
}
